import {
  DiagnosticCartEntry,
  DiagnosticContextV1,
  DiagnosticMoneyValue,
  DiagnosticPaymentMethodCategory,
  DiagnosticPriceContribution,
} from './types';

const PAYMENT_METHOD_LABELS: Record<DiagnosticPaymentMethodCategory, string> = {
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  credit_card: 'Credit card',
  gift_card: 'Gift card',
  paypal: 'PayPal',
  venmo: 'Venmo',
  cash_app: 'Cash App',
  cash: 'Cash',
};

const IDENTIFIER_CHARACTER = /^[A-Za-z0-9]$/;

const escapeMarkdown = (value: unknown): string =>
  String(value)
    .replace(/\r?\n/g, ' ')
    .replace(/\\/g, '\\\\')
    .replace(/_+/g, (underscores, offset, source: string) => {
      const previousCharacter = source[offset - 1];
      const nextCharacter = source[offset + underscores.length];
      return IDENTIFIER_CHARACTER.test(previousCharacter) &&
        IDENTIFIER_CHARACTER.test(nextCharacter)
        ? underscores
        : '\\_'.repeat(underscores.length);
    })
    .replace(/([`*{}[\]()#+!|>~])/g, '\\$1');

const line = (label: string, value: unknown): string | undefined =>
  value === undefined || value === null || value === ''
    ? undefined
    : `- **${escapeMarkdown(label)}:** ${escapeMarkdown(value)}`;

const money = (value?: DiagnosticMoneyValue): string | undefined => {
  return value?.availability && value.availability !== 'available'
    ? undefined
    : value?.formatted || undefined;
};

const contributionLines = (
  contributions: DiagnosticPriceContribution[] | undefined,
  indent = '  '
): string[] =>
  (contributions ?? []).flatMap((contribution, index) => {
    const description = [
      contribution.name,
      contribution.kind,
      contribution.id ? `id=${contribution.id}` : undefined,
      contribution.sanityId ? `Sanity ID=${contribution.sanityId}` : undefined,
      contribution.plu ? `PLU=${contribution.plu}` : undefined,
      contribution.quantity !== undefined ? `qty=${contribution.quantity}` : undefined,
      contribution.multiplier !== undefined ? `multiplier=${contribution.multiplier}` : undefined,
      money(contribution.price),
    ]
      .filter(Boolean)
      .join(' | ');
    return description ? [`${indent}- Selection ${index + 1}: ${escapeMarkdown(description)}`] : [];
  });

type CompactCartChild =
  | {
      kind: 'child';
      entry: DiagnosticCartEntry;
    }
  | {
      kind: 'modifier';
      option: DiagnosticCartEntry;
      selection?: DiagnosticCartEntry;
    };

const normalizedCartEntryType = (entry: DiagnosticCartEntry): string =>
  entry.type?.toLowerCase() ?? '';

const isCompactCartEntry = (entry: DiagnosticCartEntry): boolean =>
  entry.quantity !== 0 && Boolean(entry.sanityId || entry.plu);

const collectCompactCartChildren = (
  entries: DiagnosticCartEntry[] | undefined
): CompactCartChild[] =>
  (entries ?? []).flatMap(entry => {
    const type = normalizedCartEntryType(entry);
    const children = entry.children ?? [];

    if (type === 'itemoption') {
      const selections = children.filter(
        child =>
          normalizedCartEntryType(child) === 'itemoptionmodifier' && isCompactCartEntry(child)
      );
      const unpairedChildren = children.filter(child => !selections.includes(child));

      return [
        ...(selections.length
          ? selections.map(
              (selection): CompactCartChild => ({
                kind: 'modifier',
                option: entry,
                selection,
              })
            )
          : isCompactCartEntry(entry)
          ? [{ kind: 'modifier' as const, option: entry }]
          : []),
        ...selections.flatMap(selection => collectCompactCartChildren(selection.children)),
        ...collectCompactCartChildren(unpairedChildren),
      ];
    }

    return [
      ...(type !== 'comboslot' && isCompactCartEntry(entry)
        ? [
            type === 'itemoptionmodifier'
              ? ({ kind: 'modifier', option: entry } as const)
              : ({ kind: 'child', entry } as const),
          ]
        : []),
      ...collectCompactCartChildren(children),
    ];
  });

const compactCartChildLines = (
  entries: DiagnosticCartEntry[] | undefined,
  indent: string
): string[] => {
  let childIndex = 0;
  let modifierIndex = 0;

  return collectCompactCartChildren(entries).map(child => {
    if (child.kind === 'modifier') {
      modifierIndex += 1;
      const optionName = child.option.name;
      const selectionName = child.selection?.name;
      const displayName =
        optionName && selectionName && optionName !== selectionName
          ? `${optionName} = ${selectionName}`
          : selectionName || optionName;
      const sanityId = child.selection?.sanityId || child.option.sanityId;
      const plu = child.selection?.plu || child.option.plu;
      const description = [
        displayName,
        sanityId ? `Sanity ID=${sanityId}` : undefined,
        plu ? `PLU=${plu}` : undefined,
      ]
        .filter(Boolean)
        .join(' | ');

      return `${indent}- Modifier ${modifierIndex}: ${escapeMarkdown(description)}`;
    }

    childIndex += 1;
    const description = [
      child.entry.name,
      child.entry.sanityId ? `Sanity ID=${child.entry.sanityId}` : undefined,
      child.entry.plu ? `PLU=${child.entry.plu}` : undefined,
    ]
      .filter(Boolean)
      .join(' | ');
    return `${indent}- Child ${childIndex}: ${escapeMarkdown(description)}`;
  });
};

const cartEntryLines = (entries: DiagnosticCartEntry[] | undefined): string[] =>
  (entries ?? []).flatMap((entry, index) => {
    const indent = '  ';
    const description = [
      entry.name,
      entry.type,
      entry.id ? `id=${entry.id}` : undefined,
      entry.cartId ? `cartId=${entry.cartId}` : undefined,
      entry.plu ? `PLU=${entry.plu}` : undefined,
      entry.quantity !== undefined ? `qty=${entry.quantity}` : undefined,
    ]
      .filter(Boolean)
      .join(' | ');
    return [
      `${indent}- Item ${index + 1}: ${escapeMarkdown(description || 'Unnamed item')}`,
      ...(money(entry.unitPrice)
        ? [`${indent}  - Unit price: ${escapeMarkdown(money(entry.unitPrice)!)}`]
        : []),
      ...(money(entry.lineTotal)
        ? [`${indent}  - Line total: ${escapeMarkdown(money(entry.lineTotal)!)}`]
        : []),
      ...compactCartChildLines(entry.children, `${indent}  `),
    ];
  });

const section = (heading: string, lines: Array<string | undefined>): string | undefined => {
  const content = lines.filter((value): value is string => Boolean(value));
  return content.length ? `## ${heading}\n${content.join('\n')}` : undefined;
};

export const serializeDiagnosticContextToMarkdown = (context: DiagnosticContextV1): string => {
  const capture = context.capture;
  const user = context.user;
  const store = context.store;
  const pdp = context.pdp;
  const cart = context.cart;
  const checkoutPayment = context.checkoutPayment;
  const pricing = context.pricing;
  const pricingDiscrepancy = context.pricingDiscrepancy;
  const checkoutPaymentDetails = checkoutPayment
    ? [
        line('State', checkoutPayment.state),
        line('Payment selector visible', checkoutPayment.selectorVisible),
        line(
          'Available methods',
          checkoutPayment.availableMethods?.map(method => PAYMENT_METHOD_LABELS[method]).join(', ')
        ),
        line(
          'Selected method',
          checkoutPayment.selectedMethod
            ? PAYMENT_METHOD_LABELS[checkoutPayment.selectedMethod]
            : undefined
        ),
        line('Apple Pay available', checkoutPayment.applePayAvailable),
        line('Google Pay available', checkoutPayment.googlePayAvailable),
      ]
        .filter((value): value is string => Boolean(value))
        .join('\n')
    : undefined;

  const sections = [
    checkoutPayment ? '# Checkout Payment' : '# Internal Jira diagnostic context',
    checkoutPaymentDetails,
    pdp
      ? section('PDP', [
          line('Implementation', pdp.implementation),
          line('Object type', pdp.menuObjectType),
          line('Root ID', pdp.rootId),
          line('Selected ID', pdp.selectedId),
          line('Sanity ID', pdp.sanityId !== pdp.selectedId ? pdp.sanityId : undefined),
          line('Item name', pdp.name),
          line('PLU', pdp.plu),
          line('Quantity', pdp.quantity),
          line('Step', pdp.step),
          line('Mode', pdp.mode),
          line('Cart entry ID', pdp.cartEntryId),
          line('Offer ID', pdp.offerId),
          line('Reward ID', pdp.rewardId),
          line('Reward benefit ID', pdp.rewardBenefitId),
          line('Base unit price', money(pdp.baseUnitPrice)),
          line('Configured unit price', money(pdp.configuredUnitPrice)),
          line('Displayed item total', money(pdp.displayedTotal)),
          ...contributionLines(pdp.selections),
        ])
      : section('Cart', [
          line('Effective implementation', cart?.effectiveImplementation),
          line('Experiment-selected implementation', cart?.experimentSelectedImplementation),
          line('Recovery override', cart?.recoveryOverride),
          line('Legacy entry count', cart?.legacyEntryCount),
          line('Minimal entry count', cart?.minimalEntryCount),
          line('RBI order ID', cart?.rbiOrderId),
          ...cartEntryLines(cart?.entries),
          ...(cart?.displayedSummaryRows ?? []).map(row =>
            line(row.label || row.kind, money(row.money))
          ),
        ]),
    section('User', [
      line('Authentication state', user?.authenticationState),
      line('Name', user?.name),
      line('Email', user?.email),
      line('Cognito ID', user?.cognitoId),
      line('Legacy Cognito ID', user?.legacyCognitoId),
      line('Loyalty ID', user?.loyaltyId),
      line('Loyalty profile loaded', user?.loyaltyProfileLoaded),
      line('Loyalty profile ID', user?.loyaltyProfileId),
      line('Auth/loyalty identities correspond', user?.identitiesCorrespond),
    ]),
    section('Store', [
      line('Selection state', store?.selectionState),
      line('Store number', store?.number),
      line('Sanity ID', store?.sanityId),
      line('Name', store?.name),
      line('Public restaurant address', store?.publicAddress),
      line('POS vendor', store?.posVendor),
      line('Service mode', store?.serviceMode),
      line('Store local time', store?.localTime),
      line('Active dayparts', store?.activeDayparts?.join(', ')),
      line('Configured dayparts', store?.configuredDayparts?.join(', ')),
      line('Availability', store?.availability),
      line('One Menu', store?.isOneMenu),
    ]),
    section('Last pricing attempt', [
      line('Logical attempt ID', pricing?.logicalAttemptId),
      line('API', pricing?.sourceApi),
      line('Started at', pricing?.attemptStartedAt),
      line('Latest request at', pricing?.latestRequestAt),
      line('Request count', pricing?.requestCount),
      line('Retry count', pricing?.retryCount),
      line('Phase', pricing?.phase),
      line('Store number', pricing?.input?.storeNumber),
      line('Service mode', pricing?.input?.serviceMode),
      line('Cart entry count', pricing?.input?.cartEntryCount),
      line('Requested total', money(pricing?.input?.requestedTotal)),
      line('Checkout reprice request ID', pricing?.input?.checkoutRepriceRequestId),
      line('Request ID', pricing?.input?.requestId),
      line('Correlation ID', pricing?.input?.correlationId),
      line('RBI order ID', pricing?.rbiOrderId),
      line('Pricing status', pricing?.pricingStatus),
      line('Order status', pricing?.orderStatus),
      line('Delivery status', pricing?.deliveryStatus),
      line('Returned total', money(pricing?.returnedTotal)),
      line('Error code', pricing?.error?.code),
      line('Error message', pricing?.error?.message),
    ]),
    section('Pricing discrepancy', [
      line(
        'Classification',
        pricingDiscrepancy?.classification === 'confirmed_price_mismatch'
          ? 'Confirmed price mismatch'
          : undefined
      ),
      line(
        'Surface',
        pricingDiscrepancy?.surface === 'cart'
          ? 'View Cart'
          : pricingDiscrepancy?.surface === 'checkout'
          ? 'Checkout'
          : undefined
      ),
      line(
        'Correlation',
        pricingDiscrepancy?.correlation === 'current_cart_request'
          ? 'Current cart request'
          : pricingDiscrepancy?.correlation === 'current_checkout_selection'
          ? 'Current checkout selection'
          : undefined
      ),
      line('Observed at', pricingDiscrepancy?.observedAt),
      line('Requested subtotal', money(pricingDiscrepancy?.requestedSubtotal)),
      line('Server subtotal', money(pricingDiscrepancy?.serverSubtotal)),
      line('Difference', money(pricingDiscrepancy?.difference)),
      line('RBI order ID', pricingDiscrepancy?.rbiOrderId),
      line('Order status', pricingDiscrepancy?.orderStatus),
    ]),
    section(
      'Collection warnings',
      (context.warnings ?? []).map(warning => `- ${escapeMarkdown(warning)}`)
    ),
    section('Capture', [
      line('Schema version', context.schemaVersion),
      line('Captured at', capture?.capturedAt),
      line('Path', capture?.pathname),
      ...Object.entries(capture?.routeParams ?? {}).map(([key, value]) =>
        line(`Route ${key}`, value)
      ),
      line('Platform', capture?.platform),
      line('OS version', capture?.osVersion),
      line('App version', capture?.appVersion),
      line('Build version', capture?.buildVersion),
      line('OTA update', capture?.otaUpdateId),
      line('OTA channel', capture?.otaChannel),
      line('Commit', capture?.commitId),
      line('EAS build', capture?.easBuildId),
      line('Environment', capture?.environment),
      line('Region', capture?.region),
      line('Locale', capture?.locale),
      ...Object.entries(capture?.variants ?? {}).map(([key, value]) =>
        line(`Variant ${key}`, value)
      ),
    ]),
  ].filter((value): value is string => Boolean(value));

  return `${sections.join('\n\n')}\n`;
};

export { escapeMarkdown };
